<!-- Responsibility: Render the reusable how-to-play guide content surface.
Scope: Guide copy and close intent only; modal lifecycle is owned by game-modal/app-shell. -->
<how-to-play-dialog class="htp-content">
  <div class="htp-header">
    <h2>How to play</h2>
    <button class="htp-close modal-close-hit" onclick={ closeDialog } aria-label="Close guide">✕</button>
  </div>

  <div class="htp-body">
    <section class="htp-section">
      <h3>The loop</h3>
      <ol>
        <li>Type your action in natural language.</li>
        <li>The AI resolves the outcome from character + world state + rolls.</li>
        <li>The narrator updates prose and the game auto-saves.</li>
      </ol>
      <p class="htp-note">Use your player card in the left log panel to open the full sheet and inventory. Right sidebar stays focused on <strong>Quests</strong> and <strong>Atlas</strong>.</p>
    </section>

    <section class="htp-section">
      <h3>Combat flow</h3>
      <ol>
        <li>Pick intent: <strong>ATTACK</strong>, <strong>ABILITY</strong>, <strong>ITEM</strong>, or custom action.</li>
        <li>Resolve with the roll wizard (<strong>DC + bonus</strong>).</li>
        <li>Read immediate result in combat log/vitals inside the same modal.</li>
      </ol>
    </section>

    <section class="htp-section">
      <h3>Hub &amp; world reuse</h3>
      <ul class="htp-structured-list">
        <li><span class="htp-list-label">RESUME GAME</span><span class="htp-list-desc">Continue from latest save slots.</span></li>
        <li><span class="htp-list-label">WORLD GALLERY</span><span class="htp-list-desc">Pick a world before forging a new vessel.</span></li>
      </ul>
      <p class="htp-note">World Gallery path: <span class="htp-inline-path">Hub → World Gallery → Forge → Roll Vessel</span>.</p>
    </section>

    <section class="htp-section">
      <h3>Hero buttons & tooltips</h3>
      <ul class="htp-structured-list">
        <li>
          <span class="htp-list-label">Primary hero action</span>
          <span class="htp-list-desc">Re-enters the live scenario with your history. The tooltip now reads “Re-enter the live scenario with your history.”</span>
        </li>
        <li>
          <span class="htp-list-label">Secondary hero action</span>
          <span class="htp-list-desc">Opens the World Gallery so you can forge or review a reality before launching. Its tooltip says “Open the world gallery to forge or review a reality.”</span>
        </li>
        <li>
          <span class="htp-list-label">Utility buttons</span>
          <span class="htp-list-desc">The remaining hub utilities keep matching tooltips that describe what they open, keeping the hub intentions crystal clear.</span>
        </li>
        <li>
          <span class="htp-list-label">Atlas map</span>
          <span class="htp-list-desc">Use the map scroll wheel to zoom and drag or arrow keys to pan; the controls no longer invert scroll, so the experience stays consistent.</span>
        </li>
      </ul>
      <p class="htp-note">Tooltips now match their own buttons instead of pulling text from the wrong action, which makes every callout accurate when you hover the hero, save, or help controls.</p>
    </section>

    <section class="htp-section">
      <h3>Goals &amp; world log</h3>
      <p>Track objectives in the quest panel and use the <strong>World log</strong> strip on the left sidebar for global events.</p>
    </section>
  </div>

  <script>
    export default {
      async closeDialog() {
        await this.props.requestModalClose?.('close-button')
      },
    }
  </script>

  <style>
    how-to-play-dialog {
      display: flex;
      flex-direction: column;
      min-height: 0;
      min-width: 0;
    }

    .htp-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.15rem 0 var(--space-sm);
      border-bottom: 1px solid var(--border, #1e2a3a);

      h2 {
        margin: 0;
        font-size: var(--space-sm);
        letter-spacing: 0.2em;
        color: var(--gold, var(--gold));
      }
    }

    .htp-close {
      background: transparent;
      border: 1px solid var(--border, #1e2a3a);
      color: var(--text-muted, var(--text-dim));
      border-radius: 0.1875rem;
      font-size: 0.65rem;
      line-height: 1;
      padding: var(--space-xs) var(--space-xs);
      cursor: pointer;
    }

    .htp-body {
      margin-top: 0.9rem;
      display: grid;
      gap: 0.9rem;
      overflow-y: auto;
      min-height: 0;
      padding-right: 0.1rem;
    }

    .htp-section {
      border: 1px solid color-mix(in srgb, var(--border) var(--alpha-focus), transparent);
      border-radius: 0.375rem;
      padding: var(--space-sm);
      background: color-mix(in srgb, var(--surface-card-2) var(--alpha-high), var(--bg));

      h3 {
        margin: 0 0 var(--space-xs);
        font-size: 0.68rem;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--gold);
      }

      p, li {
        margin: 0;
        color: var(--text-muted);
        line-height: 1.5;
        font-size: 0.82rem;
      }

      ol, ul {
        margin: var(--space-xs) 0 0;
        padding-left: 1.1rem;
        display: grid;
        gap: var(--space-xs);
      }
    }

    .htp-note {
      margin-top: var(--space-xs) !important;
      color: color-mix(in srgb, var(--text-prose) var(--alpha-high), #dbe8ff) !important;
      font-size: var(--space-sm) !important;
    }

    .htp-inline-path {
      color: color-mix(in srgb, var(--gold) var(--alpha-subtle), var(--text-prose));
      font-weight: 650;
      letter-spacing: 0.03em;
    }

    .htp-structured-list {
      list-style: none;
      padding: 0;
      margin: var(--space-xs) 0 0;
      display: grid;
      gap: var(--space-xs);
    }

    .htp-structured-list li {
      display: grid;
      grid-template-columns: minmax(0, auto) 1fr;
      gap: var(--space-xs);
      align-items: baseline;
    }

    .htp-list-label {
      color: color-mix(in srgb, var(--gold) var(--alpha-high), var(--text-prose));
      font-size: 0.64rem;
      letter-spacing: 0.11em;
      font-weight: 750;
      text-transform: uppercase;
    }

    .htp-list-desc {
      color: var(--text-muted);
      font-size: var(--space-sm);
      line-height: 1.45;
    }
  </style>
</how-to-play-dialog>
